import React, { useMemo, useEffect } from 'react'
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid'
import { cx } from 'class-variance-authority'
import { usePagination } from 'react-instantsearch-hooks-web'
import { navigate } from 'gatsby'
import { useLocation } from '@gatsbyjs/reach-router'

interface Props {
  ariaLabel?: string
}

const PaginationLink = ({
  children,
  onClick,
  isCurrent = false,
  pageNumber,
  isNext = false,
  isPrevious = false,
  isLast = false,
  isFirst = false,
}: {
  children: React.ReactNode
  onClick: () => void
  isCurrent?: boolean
  pageNumber: number
  isNext?: boolean
  isPrevious?: boolean
  isLast?: boolean
  isFirst?: boolean
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cx(
      'inline-flex items-center pt-4 pl-1 font-medium border-t-2 disabled:cursor-not-allowed',
      isCurrent
        ? 'text-night border-night'
        : 'text-gray-500 hover:border-gray-300 hover:text-gray-700 border-transparent',
      (!isNext && !isPrevious && !isCurrent && !isLast && !isFirst) && 'hidden md:block',

    )}
    aria-current={isCurrent ? 'page' : undefined}
    aria-disabled={isCurrent}
    aria-label={`Page ${pageNumber}`}
    role="link"
    tabIndex={0}
  >
    {children}
  </button>
)

export const AlgoliaPagination = ({
  ariaLabel = 'Pagination des résultats de recherche',
}: Props) => {
  const {
    nbPages,
    currentRefinement,
    isFirstPage,
    isLastPage,
    refine,
  } = usePagination()

  const { search } = useLocation()
  const searchParams = new URLSearchParams(search)

  const defaultPage = useMemo(() => {
    const pageParam = searchParams.get('page')
    return pageParam ? parseInt(pageParam, 10) : 1
  }, [searchParams])

  const currentPage = useMemo(() => currentRefinement + 1, [currentRefinement])

  // Synchroniser l'URL avec l'état de pagination et gérer les changements de nombre de pages
  useEffect(() => {
    // Si la page courante est invalide (supérieure au nombre total de pages)
    if (currentPage > nbPages && nbPages > 0) {
      // Mettre à jour l'URL vers la première page
      searchParams.set('page', '1')
      navigate(`?${searchParams.toString()}`)
      refine(0) // refine(0) correspond à la première page
      return
    }

    // Synchronisation normale de l'URL avec l'état de pagination
    if (defaultPage !== currentRefinement + 1) {
      refine(defaultPage - 1)
    }
  }, [defaultPage, currentRefinement, refine, nbPages, currentPage, searchParams])

  const paginationLinks = useMemo(() => {
    const links = []
    const maxVisiblePages = 5
    const halfVisible = Math.floor(maxVisiblePages / 2)

    let startPage = Math.max(1, currentPage - halfVisible)
    const endPage = Math.min(nbPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    if (startPage > 1) {
      links.push(1)
      if (startPage > 2) {
        links.push('...')
      }
    }

    for (let i = startPage; i <= endPage; i += 1) {
      links.push(i)
    }

    if (endPage < nbPages) {
      if (endPage < nbPages - 1) {
        links.push('...')
      }
      links.push(nbPages)
    }

    return links
  }, [currentPage, nbPages])

  const handleClick = (page: number) => {
    searchParams.set('page', page.toString())
    navigate(`?${searchParams.toString()}`)
    refine(page - 1)
  }

  if (nbPages <= 1) {
    return null
  }

  return (
    <nav
      className="flex items-center justify-between"
      aria-label={ariaLabel}
      role="navigation"
    >
      <div className="flex flex-1">
        {!isFirstPage && (
          <button
            type="button"
            onClick={() => handleClick(currentPage - 1)}
            className="inline-flex items-center pt-4 text-xs font-medium text-night hover:text-gray-700 md:text-base disabled:cursor-not-allowed"
            aria-disabled={isFirstPage}
            aria-label="Aller à la page précédente"
            title="Aller à la page précédente"
            role="link"
            tabIndex={0}
          >
            <ArrowLeftIcon
              aria-hidden="true"
              className="w-3 h-3 mr-1 md:w-5 md:h-5 md:mr-3 text-night"
            />
            Précédent
          </button>
        )}
      </div>
      <div
        className="flex justify-center flex-1 space-x-4 md:-mt-px"
        role="list"
        aria-label="Liste des pages"
      >
        {paginationLinks.map((page, index) => page === '...' ? (
          <span
            key={`${page}-${index}`}
            className="inline-flex items-center pt-4 pl-1 font-medium text-gray-500"
            role="presentation"
            aria-hidden="true"
          >
            ...
          </span>
        ) : (
          <PaginationLink
            key={page}
            onClick={() => handleClick(page as number)}
            isCurrent={page === currentPage}
            isNext={page === currentPage + 1}
            isPrevious={page === currentPage - 1}
            isLast={page === nbPages}
            isFirst={page === 1}
            pageNumber={page as number}
          >
            {page}
          </PaginationLink>
        )
        )}
      </div>

      <div className="flex justify-end flex-1 w-0 -mt-px text-right">
        {!isLastPage && (
          <button
            type="button"
            onClick={() => handleClick(currentPage + 1)}
            className="inline-flex items-center pt-4 text-xs font-medium text-night hover:text-gray-700 md:text-base disabled:cursor-not-allowed"
            aria-disabled={isLastPage}
            aria-label="Aller à la page suivante"
            title="Aller à la page suivante"
            role="link"
            tabIndex={0}
          >
            Suivant
            <ArrowRightIcon
              aria-hidden="true"
              className="w-3 h-3 mr-1 md:w-5 md:h-5 md:mr-3 text-night"
            />
          </button>
        )}
      </div>
    </nav>
  )
}
